import React, { useId } from "react";
import { ospThemes } from "@ospweb/sdk-ui";
interface GaugeDialProps {
  /** 当前值 (0-100) */
  value: number;
  /** 状态 */
  status?: string;
  /** 仪表盘宽度（支持数字、百分比、calc等CSS值） */
  width?: number | string;
  /** 自定义类名 */
  className?: string;
  /** 是否显示值 */
  showValue?: boolean;
}

/**
 * 仪表盘组件
 * 半圆形的进度指示器，带有渐变色圆弧和动态指针
 */
export const GaugeDial: React.FC<GaugeDialProps> = ({
  value,
  status = "",
  width: propWidth,
  className,
  showValue = false,
}) => {
  // 为每个实例生成唯一的 ID，避免多个组件时的 SVG ID 冲突
  const uniqueId = useId();
  const trackGradientId = `track-gradient-${uniqueId}`;
  const progressGradientId = `progress-gradient-${uniqueId}`;

  // SVG 固定坐标系统（基于默认尺寸 124x68）
  const SVG_WIDTH = 124;
  const SVG_HEIGHT = 68;

  // 圆弧配置（固定坐标）
  const arcWidth = 8;
  const radius = 62; // SVG_WIDTH / 2
  const centerX = 62;
  const centerY = 68;

  // viewBox 配置
  const viewBoxPadding = 4;
  const viewBox = `${-viewBoxPadding} 0 ${SVG_WIDTH + viewBoxPadding * 2} ${SVG_HEIGHT + 2}`;

  // 确保值在 0-100 范围内
  const normalizedValue = Math.max(0, Math.min(100, value));

  // 计算圆弧角度 (180度半圆，从左到右)
  const rotation = (normalizedValue / 100) * 180 - 90;

  // 圆弧路径周长
  const arcLength = Math.PI * radius;

  // 指针配置（固定坐标）
  const POINTER_ORIGINAL_LENGTH = 38;
  const POINTER_GAP = 16;
  const pointerTargetLength = radius - arcWidth - POINTER_GAP;
  const pointerScale = pointerTargetLength / POINTER_ORIGINAL_LENGTH;

  // 生成刻度线
  const generateTickMarks = () => {
    const ticks = [];
    const totalTicks = 20; // 在半圆上均匀分布21个刻度
    const tickInnerRadius = radius - arcWidth - 2; // 内圈位置
    const tickOuterRadius = radius - arcWidth - 8; // 外圈位置

    for (let i = 0; i <= totalTicks; i++) {
      // 角度从180度（左侧）到0度（右侧）
      const angle = 180 - (i * 180) / totalTicks;
      const angleRad = (angle * Math.PI) / 180;

      // 计算刻度线的起点和终点
      const x1 = centerX + tickInnerRadius * Math.cos(angleRad);
      const y1 = centerY - tickInnerRadius * Math.sin(angleRad);
      const x2 = centerX + tickOuterRadius * Math.cos(angleRad);
      const y2 = centerY - tickOuterRadius * Math.sin(angleRad);

      ticks.push(
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={ospThemes.colors.border_white_transparent10}
          strokeWidth="1.5"
          strokeLinecap="round"
        />,
      );
    }

    return ticks;
  };

  return (
    <div
      className={className}
      style={{
        width:
          propWidth !== undefined
            ? typeof propWidth === "number"
              ? `${propWidth}px`
              : propWidth
            : `${SVG_WIDTH}px`,
        // 高度通过 aspect-ratio 自动计算
        aspectRatio: `${SVG_WIDTH} / ${SVG_HEIGHT}`,
        position: "relative",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* 定义渐变色 */}
        <defs>
          {/* 背景轨道渐变 */}
          <linearGradient
            id={trackGradientId}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop
              offset="0%"
              stopColor={ospThemes.colors.border_white_transparent5}
            />
            <stop
              offset="100%"
              stopColor={ospThemes.colors.border_white_transparent5}
            />
          </linearGradient>

          {/* 进度圆弧渐变 - 从红色到绿色 */}
          <linearGradient
            id={progressGradientId}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#A82D2DCC" />
            <stop offset="22.6%" stopColor="#FD8016CC" />
            <stop offset="47.8%" stopColor="#D4854ACC" />
            <stop offset="72.4%" stopColor="#2B9146CC" />
            <stop offset="98.1%" stopColor="#036263B2" />
          </linearGradient>
        </defs>

        {/* 背景轨道 - 半圆 */}
        <path
          d={`M 0 ${centerY} A ${radius} ${radius} 0 0 1 ${SVG_WIDTH} ${centerY}`}
          stroke={`url(#${trackGradientId})`}
          strokeWidth={arcWidth}
          strokeLinecap="round"
          fill="none"
        />

        {/* 刻度线 */}
        <g opacity="0.5">{generateTickMarks()}</g>

        {/* 进度圆弧 - 根据百分比裁剪 */}
        {normalizedValue > 0 && (
          <g>
            <path
              d={`M 0 ${centerY} A ${radius} ${radius} 0 0 1 ${SVG_WIDTH} ${centerY}`}
              stroke={`url(#${progressGradientId})`}
              strokeWidth={arcWidth}
              strokeLinecap="round"
              fill="none"
              strokeDasharray={`${(normalizedValue / 100) * arcLength} ${arcLength}`}
              style={{
                transition: "stroke-dasharray 0.6s ease-out",
              }}
            />
          </g>
        )}

        {/* 指针 - 水平箭头形状 */}
        {!showValue && (
          <g transform={`rotate(${rotation} ${centerX} ${centerY})`}>
            {/* 指针从圆心向上延伸，缩放后旋转-90度使其垂直 */}
            <g
              transform={`translate(${centerX}, ${centerY}) rotate(-90) translate(0, -5) scale(${pointerScale}, 1)`}
            >
              <path
                d="M37.0031 2.9213C37.5659 2.96573 38 3.43542 38 4C38 4.56458 37.5659 5.03427 37.0031 5.0787L3.98759 7.68519C1.83799 7.85489 -2.691e-07 6.15629 -1.74846e-07 4C-8.0591e-08 1.84371 1.83799 0.145103 3.98759 0.314808L37.0031 2.9213Z"
                fill={ospThemes.colors.background_white_overlay5}
              />
            </g>
          </g>
        )}
      </svg>

      {/* 数值与状态展示（覆盖在仪表盘中心） */}
      {showValue && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-[18px]">
          <span className="body_bold_m text-content_primary">
            {Math.round(normalizedValue)}
          </span>
          {status && (
            <span className="body_xs text-content_tertiary mt-[-2px]">
              {status}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
